import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createAppAsyncThunk } from 'reduxTyped'

import * as appAtypes from 'common/core/constants/appReduxActionTypes'
import { UserField } from 'shared/api/common/schemas/userField'
import { UserFieldType, UserFieldStatus } from 'shared/api/common/types'
import { getQuery } from 'utils/router/tools'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

import {
  fetchUserFields,
  createField,
  openCreateFieldModal,
  restoreField,
  ensureField,
  saveField,
  archiveField,
  deleteField,
  bulkFieldsArchive,
  bulkFieldsDelete,
  bulkFieldsRestore,
  moveToFolderUserField,
} from '../actions/userFieldsActions'

export interface UserFieldCreateModal {
  open: boolean
  title: string
  type: UserFieldType
  description: string | null
  folderPath: string
  allowedFieldTypes: UserFieldType[]
  builderId?: number
  fetching?: boolean
}

export interface UserFieldSearchValue {
  query: string
}

export interface UserFieldState {
  items: UserField[]
  byId: Record<number, UserField>
  fetching: boolean
  createModal: UserFieldCreateModal
  searchValue: UserFieldSearchValue
}

// Synchronous actions will be exported from slice
const namespace = 'userFields'

// Sockets action for notifications
export const socketsActionForUserFields = createAppAsyncThunk(
  `${namespace}/socketsActionForUserFields`,
  async (_, { dispatch }) => {
    const { path } = getQuery()
    await dispatch(fetchUserFields({ activeOnly: Boolean(path), path: path ?? '/' }))
  },
)

const fieldDefaults: Omit<UserFieldCreateModal, 'open'> = {
  title: '',
  type: UserFieldType.TEXT,
  description: '',
  folderPath: '/',
  allowedFieldTypes: [
    UserFieldType.TEXT,
    UserFieldType.NUMBER,
    UserFieldType.DATE,
    UserFieldType.DATETIME,
    UserFieldType.BOOL,
    UserFieldType.ARRAY,
  ],
}

const mergeToListItem = (state: UserFieldState, id: number, data: Partial<UserField>) => {
  if (state.items == null) {
    return state
  }

  const index = state.items.findIndex((element) => element.field_id === id)

  if (index < 0) {
    return state
  }

  const item = state.items[index]
  state.items[index] = { ...item, ...data }
  state.byId[id] = { ...item, ...data }

  return state
}

const initialState: UserFieldState = {
  items: [],
  byId: {},
  fetching: false,
  createModal: { open: false, ...fieldDefaults },
  searchValue: {
    query: '',
  },
}

const userFieldsSlice = createSlice({
  name: 'userFields',
  initialState,
  reducers: {
    setUserFieldSearchValue: (state, action: PayloadAction<string>) => {
      state.searchValue.query = action.payload
    },
    closeCreateFieldModal: (state) => {
      state.createModal = { ...state.createModal, open: false, ...fieldDefaults }
    },
    createFieldModalChange: (state, action: PayloadAction<Partial<UserField>>) => {
      state.createModal = { ...state.createModal, ...action.payload }
    },
    setUserFieldsFetchRequest: (state) => {
      state.fetching = true
    },
    archiveFieldNotification: (state, action: PayloadAction<number>) => {
      mergeToListItem(state, action.payload, { status: UserFieldStatus.ARCHIVED })
    },
    restoreFieldNotification: (state, action: PayloadAction<number>) => {
      mergeToListItem(state, action.payload, { status: UserFieldStatus.ACTIVE })
    },
    bulkFieldsArchiveNotification: (state, action: PayloadAction<UserField[]>) => {
      const models = action.payload
      models.forEach((model) => {
        mergeToListItem(state, model.field_id, { status: UserFieldStatus.ARCHIVED })
      })
    },
    bulkFieldsRestoreNotification: (state, action: PayloadAction<UserField[]>) => {
      const models = action.payload
      models.forEach((model) => {
        mergeToListItem(state, model.field_id, { status: UserFieldStatus.ACTIVE })
      })
    },
    bulkFieldsDeleteNotification: (state, action: PayloadAction<UserField[]>) => {
      const models = action.payload
      models.forEach((model) => {
        state.items = state.items.filter((item) => item.field_id !== model.field_id)
        delete state.byId[model.field_id]
      })
    },
    fieldUpdatedNotification: (state, action: PayloadAction<UserField>) => {
      mergeToListItem(state, action.payload.field_id, action.payload)
    },
    fieldDeletedNotification: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((item) => item.field_id !== action.payload)
      delete state.byId[action.payload]
    },
    resetUserFields: (state) => {
      return { ...state, ...initialState }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(appAtypes.APP_UPDATE_CURRENT_ACCOUNT_ID, (state) => {
        return { ...state, ...initialState }
      })

      .addCase(fetchUserFields.pending, (state) => {
        state.fetching = true
      })
      .addCase(fetchUserFields.fulfilled, (state, action) => {
        const byId: Record<number, UserField> = {}
        if (Array.isArray(action.payload)) {
          action.payload.forEach((item) => {
            byId[item.field_id] = item
          })
        }
        state.items = action.payload
        state.byId = byId
        state.fetching = false
      })
      .addCase(fetchUserFields.rejected, (state) => {
        state.fetching = false
      })
      .addCase(openCreateFieldModal.fulfilled, (state, action) => {
        const initialData = action.payload || {}
        let allowedFieldTypes = initialData?.allowedFieldTypes

        allowedFieldTypes =
          Array.isArray(allowedFieldTypes) && allowedFieldTypes.length > 0
            ? allowedFieldTypes
            : fieldDefaults.allowedFieldTypes

        state.createModal = {
          ...state.createModal,
          open: true,
          builderId: initialData.builderId,
          title: fieldDefaults.title,
          type: (initialData.type as UserFieldType) || allowedFieldTypes[0] || fieldDefaults.type,
          folderPath: initialData.folderPath || fieldDefaults.folderPath,
          allowedFieldTypes: allowedFieldTypes,
        }
      })
      .addCase(createField.pending, (state) => {
        state.createModal.fetching = true
      })
      .addCase(createField.fulfilled, (state, action) => {
        state.items.push(action.payload)
        state.byId[action.payload.field_id] = action.payload
        state.createModal = { ...state.createModal, fetching: false, open: false, ...fieldDefaults }
      })
      .addCase(createField.rejected, (state) => {
        state.createModal.fetching = false
      })
      .addCase(ensureField.fulfilled, (state, action) => {
        mergeToListItem(state, action.payload.field_id, action.payload)
      })
      .addCase(saveField.fulfilled, (state, action) => {
        mergeToListItem(state, action.payload.field_id, action.payload)
      })
      .addCase(archiveField.fulfilled, (state, action) => {
        mergeToListItem(state, action.payload.field_id, { status: UserFieldStatus.ARCHIVED })
      })
      .addCase(restoreField.fulfilled, (state, action) => {
        mergeToListItem(state, action.meta.arg, { status: UserFieldStatus.ACTIVE })
      })
      .addCase(deleteField.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.field_id !== action.meta.arg)
        delete state.byId[action.meta.arg]
      })
      .addCase(bulkFieldsArchive.fulfilled, (state, action) => {
        const models = action.payload
        models.forEach((model) => {
          mergeToListItem(state, model.field_id, { status: UserFieldStatus.ARCHIVED })
        })
      })
      .addCase(bulkFieldsRestore.fulfilled, (state, action) => {
        const models = action.payload
        models.forEach((model) => {
          mergeToListItem(state, model.field_id, { status: UserFieldStatus.ACTIVE })
        })
      })
      .addCase(bulkFieldsDelete.fulfilled, (state, action) => {
        const models = action.payload
        models.forEach((model) => {
          state.items = state.items.filter((item) => item.field_id !== model.field_id)
          delete state.byId[model.field_id]
        })
      })
      .addCase(moveToFolderUserField.pending, (state) => {
        state.fetching = true
      })
      .addCase(moveToFolderUserField.fulfilled, (state) => {
        state.fetching = false
      })
      .addCase(moveToFolderUserField.rejected, (state) => {
        state.fetching = false
      })
  },
})

export const {
  setUserFieldSearchValue,
  closeCreateFieldModal,
  createFieldModalChange,
  setUserFieldsFetchRequest,
  archiveFieldNotification,
  restoreFieldNotification,
  bulkFieldsArchiveNotification,
  bulkFieldsRestoreNotification,
  bulkFieldsDeleteNotification,
  fieldUpdatedNotification,
  fieldDeletedNotification,
  resetUserFields,
} = userFieldsSlice.actions

// Notification listeners
anotherTabNotificationsListener.on('cuf_archived', (data, dispatch) => {
  dispatch(archiveFieldNotification(data.model.field_id))
})

anotherTabNotificationsListener.on('cuf_restored', (data, dispatch) => {
  dispatch(restoreFieldNotification(data.model.field_id))
})

anotherTabNotificationsListener.on('cuf_created', (_, dispatch) => {
  dispatch(socketsActionForUserFields())
})

anotherTabNotificationsListener.on('cufs_moved', (_, dispatch) => {
  dispatch(socketsActionForUserFields())
})

anotherTabNotificationsListener.on('cufs_moved_bulk', (_, dispatch) => {
  dispatch(socketsActionForUserFields())
})

anotherTabNotificationsListener.on('cuf_updated', (data, dispatch) => {
  dispatch(fieldUpdatedNotification(data.model))
})

anotherTabNotificationsListener.on('cuf_deleted', (data, dispatch) => {
  dispatch(fieldDeletedNotification(data.model.field_id))
})

anotherTabNotificationsListener.on('cufs_archived', (data, dispatch) => {
  dispatch(bulkFieldsArchiveNotification(data.models))
})

anotherTabNotificationsListener.on('cufs_restored', (data, dispatch) => {
  dispatch(bulkFieldsRestoreNotification(data.models))
})

anotherTabNotificationsListener.on('cufs_deleted', (data, dispatch) => {
  dispatch(bulkFieldsDeleteNotification(data.models))
})

export default userFieldsSlice.reducer
